import React from 'react';
import PageLayout from '../../components/PageLayout';
import PageHero from '../../components/PageHero';

const BREADCRUMBS = [
  { label: 'Home', to: '/' },
  { label: 'Services', to: '/services/ifys-services' },
  { label: 'Youth Services' },
];

const SocialIcons = ({ facebook, instagram, name }) => {
  if (!facebook && !instagram) return null;
  return (
    <div className="flex gap-3 mt-4">
      {facebook && (
        <a
          href={facebook}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${name} on Facebook`}
          title={`${name} on Facebook`}
          className="text-ifys-blue hover:text-ifys-blue/70 transition-colors focus:outline-none focus:ring-2 focus:ring-ifys-blue rounded"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg>
        </a>
      )}
      {instagram && (
        <a
          href={instagram}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${name} on Instagram`}
          title={`${name} on Instagram`}
          className="text-ifys-blue hover:text-ifys-blue/70 transition-colors focus:outline-none focus:ring-2 focus:ring-ifys-blue rounded"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
        </a>
      )}
    </div>
  );
};

const ContactBlock = ({ email, label, social }) => (
  <div className="mt-6 p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
    <p className="text-sm text-gray-700 leading-relaxed">
      Contact <a href={`mailto:${email}`} className="text-ifys-blue font-semibold hover:underline">{email}</a> or{' '}
      <a href="tel:0754383000" className="text-ifys-blue font-semibold hover:underline">07 5438 3000</a> and ask
      to speak with the {label}, for information about eligibility and referrals.
    </p>
    {social && <SocialIcons {...social} />}
  </div>
);

const YouthServicesPage = () => {
  return (
    <PageLayout activeLink="services">
      <PageHero
        title="Youth Services"
        backgroundImage="/images/service-headers/youth_services%201.jpg"
        breadcrumbs={BREADCRUMBS}
        variant="split"
      />

      {/* Intro */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ifys-blue mb-6">
          Youth Services
        </h2>
      </section>

      {/* Young Parents Supports Program */}
      <section className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-ifys-blue mb-6">
            Young Parents Supports Program
          </h3>
          <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
            <p>
              IFYS&rsquo;s Young Parents Support Program (YPSP) supports young mums and dads on the
              Sunshine Coast, up to 25 years of age, through their pregnancy, and in raising their
              children, up until their children turn 5.
            </p>
            <p>
              YPSP offers some outreach support and group programs tailored to the needs of young
              pregnant and parenting families. YPSP provides a safe space for families to engage in
              activities that strengthen their capability to parent, to build social networks, and to
              access information and support in their community.
            </p>
            <p>
              Groups connect participants with other young parents to create strong, aligned
              connections as well as allow specialist services and community agencies to present
              information and share wisdom on relevant topics such as relationships, budgeting,
              life/family/parenting skills. Morning Tea and a healthy lunch is provided and we
              encourage parents to relax and enjoy a meal together. Families are supported to identify
              and work towards goals that aim to assist participants to reach their full potential.
              YPSP is an inclusive, non-judgemental and safe space for young parents to be themselves
              and form social and community connections.
            </p>
          </div>
          <ContactBlock
            email="ypsp@ifys.com.au"
            label="Young Parents Support Program Team"
            social={{
              name: 'Young Parents Support Program',
              facebook: 'https://www.facebook.com/youngparentssupportprogram',
              instagram: 'https://www.instagram.com/youngparentssupportprogram/',
            }}
          />
        </div>
      </section>

      {/* Youth Transition */}
      <section>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-ifys-blue mb-4">
            Youth Transition
          </h3>
          <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-8">
            IFYS&rsquo;s Youth Transition Services (YTS) support young people aged 12-24 years.
            Support, advocacy and practical assistance, is offered through the following service
            delivery under the program of YTS:
          </p>

          <div className="space-y-10">
            {/* YELS */}
            <div>
              <h4 className="text-xl font-bold text-ifys-blue mb-3">
                Youth Engagement and Linkage Service
              </h4>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                The Youth Engagement and Linkage Service (YELS) provides support and outreach,
                working on a one-on-one basis with young people and in group settings across the
                Sunshine Coast. YELS supports young people from 12 - 21 years old who are engaging in
                high-risk behaviours, and those who are disconnected or at risk of disconnecting from
                family, school or their community. The Program provides case management, advice and
                advocacy and assists young people to make connections with local services.
              </p>
              <ContactBlock
                email="yels@ifys.com.au"
                label="Youth Transitions Services Team"
                social={{
                  name: 'YELS Queensland',
                  facebook: 'https://www.facebook.com/YELSQLD',
                  instagram: 'https://www.instagram.com/yelsqld/',
                }}
              />
            </div>

            {/* Beyond Expectations */}
            <div>
              <h4 className="text-xl font-bold text-ifys-blue mb-3">Beyond Expectations</h4>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                Beyond Expectations provides case management to young people aged 15-19 and is
                primarily focused around supporting young people to reach their employment and/or
                training goals. Young people are also able to complete a variety of training modules
                in order to gain skills, knowledge and confidence to obtain employment.
              </p>
              <ContactBlock
                email="beyondexpectations@ifys.com.au"
                label="Youth Transitions Services Team"
              />
            </div>

            {/* Fresh Start */}
            <div>
              <h4 className="text-xl font-bold text-ifys-blue mb-3">Fresh Start</h4>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                Fresh Start provides case management to young people aged 15-24 and is primarily
                focused around supporting young people to reach their employment and/or training
                goals. Young people are also able to complete a variety of training modules in order
                to increase their skills, knowledge and confidence. This program looks to work with
                young people whose behaviours, needs or environments, may see them engaged with or at
                risk of engagement with Queensland Police, Youth Justice or Community Corrections.
              </p>
              <ContactBlock
                email="freshstartsc@ifys.com.au"
                label="Youth Transitions Services Team"
              />
            </div>

            {/* Reconnect */}
            <div>
              <h4 className="text-xl font-bold text-ifys-blue mb-3">Reconnect</h4>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                The Reconnect program, working with schools in the Caloundra Electorate, assists
                young people to remain at school, connected to their families and community and to
                address the risk of homelessness occurring. The program provides advice and advocacy,
                assists young people to make connections with local services and can offer more
                intensive support and family mediation if required. The program works with young
                people aged 12-18 who are homeless or at risk of homelessness.
              </p>
              <ContactBlock
                email="reconnect@ifys.com.au"
                label="Youth Transitions Services Team"
              />
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default YouthServicesPage;
